import Dialog, {
  DialogBody,
  DialogHeader,
  DialogHeading,
  DialogSubheading,
} from '@raydiant/raydial/components/Dialog';
import Modal from '@raydiant/raydial/components/Modal';
import * as paths from '../../routes/paths';
import history from '../../history';
import { Device } from '@raydiant/api-client-js';
import ProductHubCard from '../../components/ProductHubCard';
import Button from '@raydiant/raydial/components/Button';
import { Trash } from '@raydiant/raydial/components/Icons';
import Text from '@raydiant/raydial/components/Text';
import { useState } from 'react';
import LoadingModal from '@raydiant/raydial/components/LoadingModal';
import { useToastState } from '@raydiant/raydial/components/Toast';
import { capitalize } from '../../utilities/stringUtils';
import TextField from '@raydiant/raydial/components/TextField';
import moment from 'moment';
import useDeleteDevicePaymentTerminal from '../../hooks/useDeleteDevicePaymentTerminal';
import config from '../../config';

const { publicAssetUrl } = config;

interface PaymentTerminalSettingsProps {
  isOpen: boolean;
  device: Device;
}

const PaymentTerminalSettings = ({
  isOpen,
  device,
}: PaymentTerminalSettingsProps) => {
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] =
    useState(false);
  const [deleteConfirmationModalStep, setDeleteConfirmationModalStep] =
    useState(1);
  const [isDeletingPaymentTerminal, setIsDeletingPaymentTerminal] =
    useState(false);
  const toastState = useToastState();
  const [confirmDeleteTextFieldValue, setConfirmDeleteTextFieldValue] =
    useState('');
  const deleteDevicePaymentTerminal = useDeleteDevicePaymentTerminal();

  const terminalInfo = {
    name: device.name,
    dateAdded: device.terminal?.createdAt
      ? moment(device.terminal.createdAt).format('MMMM D, YYYY')
      : '',
    //TODO: This is hardcoded until we work on the next feature which will include lightspeed
    imgSrc: `${publicAssetUrl}/connectors/square.svg`,
    connectorName: 'square',
  };

  const handleDeleteTerminal = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsDeletingPaymentTerminal(true);
    try {
      await deleteDevicePaymentTerminal.mutate(device.id);
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeletingPaymentTerminal(false);
      setDeleteConfirmationModalOpen(false);
      toastState.add({
        title: 'Payment Terminal Deleted',
        description: `${capitalize(
          terminalInfo.connectorName,
        )} payment terminal deleted successfully`,
      });

      window.location.href = paths.screens.pattern;
    }
  };

  const openConfirmDeleteModal = () => {
    setDeleteConfirmationModalOpen(true);
  };

  const closeConfirmDeleteModal = () => {
    setDeleteConfirmationModalOpen(false);
  };

  const incrementStep = () => {
    setDeleteConfirmationModalStep(deleteConfirmationModalStep + 1);
  };

  const renderTrashIcon = () => {
    return (
      <Button
        variant="unstyled"
        className="text-gray-300 hover:text-red-600"
        onPress={openConfirmDeleteModal}
        // TODO: Requires API change
        // isDisabled={terminalInfo.status !== 'online'}
      >
        <Trash className="size-5" />
      </Button>
    );
  };

  const renderDeleteTerminalModal = () => {
    const isDeleteTextFieldValidated =
      confirmDeleteTextFieldValue.toUpperCase() === 'DELETE';

    return (
      <Modal isOpen={deleteConfirmationModalOpen}>
        <LoadingModal isOpen={isDeletingPaymentTerminal} />
        <Dialog>
          <DialogHeader>
            <DialogHeading>Confirm delete payment terminal</DialogHeading>
          </DialogHeader>

          {deleteConfirmationModalStep === 1 && (
            <DialogBody
              className="flex gap-4 mb-8"
              id="step-1-form"
              as="form"
              onSubmit={incrementStep}
            >
              <div className="p-4">
                <Text as="p">
                  Deleting this payment terminal to another kiosk may affect
                  kiosk currently connected.
                </Text>
                <br />
                <br />
                <Text as="p">Are you sure you want to proceed?</Text>
              </div>
            </DialogBody>
          )}

          {deleteConfirmationModalStep === 2 && (
            <DialogBody
              className="flex flex-col gap-4 mb-8 px-4"
              id="step-2-form"
              as="form"
              onSubmit={handleDeleteTerminal}
            >
              <Text as="p">To proceed, please type DELETE below:</Text>
              <TextField
                aria-label="confirm deletion"
                value={confirmDeleteTextFieldValue}
                onChange={setConfirmDeleteTextFieldValue}
              />
            </DialogBody>
          )}

          <footer className="flex flex-wrap gap-6 pb-4 px-4">
            <Button
              size="large"
              variant="secondary"
              className="flex-1"
              onPress={closeConfirmDeleteModal}
            >
              Cancel
            </Button>
            {deleteConfirmationModalStep === 1 && (
              <Button
                size="large"
                variant="caution"
                type="submit"
                form="step-1-form"
                className="flex-1"
              >
                Confirm Delete
              </Button>
            )}

            {deleteConfirmationModalStep === 2 && (
              <Button
                size="large"
                variant="caution"
                type="submit"
                form="step-2-form"
                className="flex-1"
                isDisabled={!isDeleteTextFieldValidated}
              >
                Delete Terminal
              </Button>
            )}
          </footer>
        </Dialog>
      </Modal>
    );
  };

  // TODO: Requires API change
  // const renderTerminalStatus = () => {
  //   if (terminalInfo.status === 'online') {
  //     return (
  //       <div className="flex items-center">
  //         <CheckCircle className="size-4 mr-1" />
  //         <span className="capitalize">{terminalInfo.status}</span>
  //       </div>
  //     );
  //   }

  //   return (
  //     <DialogTrigger>
  //       <Button
  //         variant="unstyled"
  //         aria-label="error"
  //         className="flex items-center"
  //       >
  //         <div className="size-4 mr-1">
  //           {terminalInfo.status === 'offline' && (
  //             <InfoCircle className="size-[inherit]" />
  //           )}
  //           {terminalInfo.status === 'error' && (
  //             <InfoCircle className="size-[inherit]" />
  //           )}
  //         </div>
  //         <span className="capitalize">{terminalInfo.status}</span>
  //       </Button>
  //       <Popover className="max-w-[250px]">
  //         <Dialog className="text-black">
  //           {terminalInfo.status === 'offline' && (
  //             <div>
  //               <Text className="font-semibold mb-2" as="p">
  //                 Payment terminal offline
  //               </Text>
  //               <Text as="p">
  //                 This terminal is offline. Please check your Square dashboard
  //                 <Link href="//app.squareup.com/login" target="_blank">
  //                   Login to Square
  //                 </Link>
  //               </Text>
  //             </div>
  //           )}
  //           {terminalInfo.status === 'error' && (
  //             <div>
  //               <Text className="font-semibold mb-2" as="p">
  //                 Payment Terminal Error
  //               </Text>
  //               <Text as="p">
  //                 This terminal is unavailable. Please check your Square
  //                 dashboard or contact{' '}
  //                 <Link href="mailto:support@raydiant.com">
  //                   Raydiant Support
  //                 </Link>
  //               </Text>
  //             </div>
  //           )}
  //         </Dialog>
  //       </Popover>
  //     </DialogTrigger>
  //   );
  // };

  const renderTerminalDescription = () => {
    return (
      <span className="flex">
        {`${capitalize(terminalInfo.connectorName)} terminal`}
        &nbsp;&nbsp;|&nbsp;&nbsp;Added {terminalInfo.dateAdded}
        &nbsp;&nbsp;
        {/* TODO: Requires API change */}
        {/* |&nbsp;&nbsp; */}
        {/* <span
          className={cn('inline-flex items-center mr-1', {
            'text-red-600': terminalInfo.status !== 'online',
            'text-green-600': terminalInfo.status === 'online',
          })}
        >
          {renderTerminalStatus()}
        </span> */}
      </span>
    );
  };

  const handleClose = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    history.push(paths.screens.pattern);
  };

  return (
    <Modal isOpen={isOpen}>
      {renderDeleteTerminalModal()}
      <Dialog>
        <DialogHeader>
          <DialogHeading>Payment Terminal</DialogHeading>
          <DialogSubheading>Manage payment terminal</DialogSubheading>
        </DialogHeader>

        <DialogBody
          className="grid gap-4 mb-8"
          id="terminal-form"
          onSubmit={handleClose}
          as="form"
        >
          <div className="px-4">
            <ProductHubCard
              imgSrc={terminalInfo.imgSrc}
              title={terminalInfo.name}
              description={renderTerminalDescription()}
              outline
              rightAction={renderTrashIcon()}
            />
          </div>
        </DialogBody>

        <footer className="flex flex-wrap gap-6 pb-4 px-4">
          <Button
            size="large"
            variant="primary"
            className="grow"
            type="submit"
            form="terminal-form"
          >
            Done
          </Button>
        </footer>
      </Dialog>
    </Modal>
  );
};

export default PaymentTerminalSettings;
